import React, { useEffect, useState } from 'react';
import { EFFECT_OPTIONS, ENVIRONMENT_OPTIONS, MCP_PUBLIC_URL, RISK_OPTIONS, SINGLETON_ENDPOINT_ID } from '../governedMcpConstants';
import { createId } from '../governedMcpStore';
import type {
  AccessPolicy,
  DirectoryGroup,
  DirectoryUser,
  McpClient,
  McpEnvironment,
  PublishedCapability,
} from '../governedMcpTypes';
import { Scale } from 'lucide-react';
import { KindBadge, RepresentationBadge } from '../components/StatusBadges';
import { CheckRow, FormSection, FormStack, GatewayDrawer, ToggleRow, fieldClass, ghostBtn, labelClass, panelClass, primaryBtn } from '../components/GatewayUi';

function blankPolicy(): AccessPolicy {
  return {
    id: createId('pol'),
    name: '',
    description: '',
    userIds: [],
    groupIds: [],
    clientIds: [],
    endpointIds: [SINGLETON_ENDPOINT_ID],
    publishedCapabilityIds: [],
    allowDiscovery: true,
    allowInvocation: true,
    readOnly: true,
    environments: ['production'],
    timeWindow: 'Always',
    rateLimitPerMinute: 30,
    maxRecords: 25,
    riskLevel: 'medium',
    requiresApproval: false,
    sensitiveDataRestricted: true,
    outputRestricted: true,
    inheritSelficPermissions: true,
    effect: 'allow',
    status: 'active',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    createdBy: 'Current administrator',
  };
}

function toggle<T>(list: T[], value: T): T[] {
  return list.includes(value) ? list.filter((item) => item !== value) : [...list, value];
}

export default function PolicyFormDrawer({
  open,
  initial,
  users,
  groups,
  clients,
  published,
  isLight,
  onClose,
  onSave,
}: {
  open: boolean;
  initial: AccessPolicy | null;
  users: DirectoryUser[];
  groups: DirectoryGroup[];
  clients: McpClient[];
  published: PublishedCapability[];
  isLight: boolean;
  onClose: () => void;
  onSave: (policy: AccessPolicy) => void;
}) {
  const [form, setForm] = useState<AccessPolicy>(blankPolicy);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setForm(initial ? { ...initial, endpointIds: [SINGLETON_ENDPOINT_ID] } : blankPolicy());
      setError(null);
    }
  }, [open, initial]);

  const submit = () => {
    if (!form.name.trim()) {
      setError('Policy name is required.');
      return;
    }
    onSave({ ...form, endpointIds: [SINGLETON_ENDPOINT_ID] });
  };

  return (
    <GatewayDrawer
      open={open}
      eyebrow="Governed MCP / Policies"
      icon={<Scale size={18} className="text-indigo-500" />}
      title={initial ? form.name || 'Edit access policy' : 'Create MCP access policy'}
      subtitle="Subject + client + capability + conditions → Allow, Deny, or Require approval."
      status={{
        label: form.effect === 'allow' ? 'Allow' : form.effect === 'deny' ? 'Deny' : 'Approval',
        tone: form.effect === 'deny' ? 'warn' : form.effect === 'allow' ? 'success' : 'neutral',
      }}
      isLight={isLight}
      size="xl"
      onClose={onClose}
      footer={
        <>
          <button type="button" className={ghostBtn(isLight)} onClick={onClose}>Cancel</button>
          <button type="button" className={primaryBtn()} onClick={submit}>{initial ? 'Save policy' : 'Create policy'}</button>
        </>
      }
    >
      {error ? <p className="mb-4 text-sm text-red-500">{error}</p> : null}
      <FormStack>
        <div className="space-y-5">
          <FormSection title="Policy" description="Name the rule and its default effect." isLight={isLight}>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className={labelClass(isLight)}>Policy name</label>
                <input className={fieldClass(isLight)} value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} />
              </div>
              <div>
                <label className={labelClass(isLight)}>Effect</label>
                <select className={fieldClass(isLight)} value={form.effect} onChange={(e) => setForm((p) => ({ ...p, effect: e.target.value as AccessPolicy['effect'] }))}>
                  {EFFECT_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="mt-4">
              <label className={labelClass(isLight)}>Description</label>
              <textarea className={fieldClass(isLight)} rows={2} value={form.description} onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))} />
            </div>
          </FormSection>

          <FormSection title="Subjects" description="Users and groups this policy applies to." isLight={isLight}>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <p className={labelClass(isLight)}>Users</p>
                {users.map((user) => (
                  <CheckRow key={user.id} label={user.name} checked={form.userIds.includes(user.id)} onChange={() => setForm((p) => ({ ...p, userIds: toggle(p.userIds, user.id) }))} isLight={isLight} />
                ))}
              </div>
              <div>
                <p className={labelClass(isLight)}>Groups</p>
                {groups.map((group) => (
                  <CheckRow key={group.id} label={group.name} checked={form.groupIds.includes(group.id)} onChange={() => setForm((p) => ({ ...p, groupIds: toggle(p.groupIds, group.id) }))} isLight={isLight} />
                ))}
              </div>
            </div>
          </FormSection>

          <FormSection title="Scope" description="Clients and published capabilities on the MCP host." isLight={isLight}>
            <div>
              <p className={labelClass(isLight)}>AI clients</p>
              <div className="grid sm:grid-cols-2 gap-1">
                {clients.map((client) => (
                  <CheckRow key={client.id} label={client.name} checked={form.clientIds.includes(client.id)} onChange={() => setForm((p) => ({ ...p, clientIds: toggle(p.clientIds, client.id) }))} isLight={isLight} />
                ))}
              </div>
              {!clients.length ? <p className="text-xs text-amber-600">Register a client first.</p> : null}
            </div>
            <div className="mt-4">
              <p className={labelClass(isLight)}>Published capabilities</p>
              <div className="space-y-2 max-h-56 overflow-auto">
                {published.map((item) => (
                  <label
                    key={item.id}
                    className={`flex items-start gap-3 rounded-xl border p-3 ${
                      form.publishedCapabilityIds.includes(item.id)
                        ? isLight ? 'border-neutral-300 bg-neutral-50' : 'border-neutral-700 bg-neutral-800/40'
                        : isLight ? 'border-neutral-200' : 'border-neutral-800'
                    }`}
                  >
                    <input
                      type="checkbox"
                      className="mt-1"
                      checked={form.publishedCapabilityIds.includes(item.id)}
                      onChange={() => setForm((p) => ({ ...p, publishedCapabilityIds: toggle(p.publishedCapabilityIds, item.id) }))}
                    />
                    <span className="min-w-0">
                      <span className={`block text-sm font-medium ${isLight ? 'text-neutral-900' : 'text-white'}`}>{item.mcpToolName}</span>
                      <span className={`block text-xs ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>{item.description}</span>
                      <span className="flex flex-wrap gap-1 mt-1">
                        <KindBadge kind={item.capabilityKind} />
                        {item.mcpRepresentations.map((rep) => (
                          <RepresentationBadge key={rep} value={rep} />
                        ))}
                      </span>
                    </span>
                  </label>
                ))}
                {!published.length ? <p className="text-xs text-amber-600">Publish a capability first.</p> : null}
              </div>
            </div>
          </FormSection>

          <FormSection title="Conditions" description="When and how this policy may fire." isLight={isLight}>
            <div>
              <p className={labelClass(isLight)}>Environments</p>
              <div className="flex flex-wrap gap-3">
                {ENVIRONMENT_OPTIONS.map((option) => (
                  <CheckRow
                    key={option.value}
                    label={option.label}
                    checked={form.environments.includes(option.value)}
                    onChange={() => setForm((p) => ({ ...p, environments: toggle(p.environments, option.value as McpEnvironment) }))}
                    isLight={isLight}
                  />
                ))}
              </div>
            </div>
            <div className="grid sm:grid-cols-3 gap-3 mt-4">
              <div>
                <label className={labelClass(isLight)}>Time window</label>
                <input className={fieldClass(isLight)} value={form.timeWindow} onChange={(e) => setForm((p) => ({ ...p, timeWindow: e.target.value }))} />
              </div>
              <div>
                <label className={labelClass(isLight)}>Rate limit / min</label>
                <input type="number" className={fieldClass(isLight)} value={form.rateLimitPerMinute} onChange={(e) => setForm((p) => ({ ...p, rateLimitPerMinute: Number(e.target.value) || 0 }))} />
              </div>
              <div>
                <label className={labelClass(isLight)}>Max records</label>
                <input type="number" className={fieldClass(isLight)} value={form.maxRecords} onChange={(e) => setForm((p) => ({ ...p, maxRecords: Number(e.target.value) || 0 }))} />
              </div>
            </div>
            <div className="mt-4">
              <label className={labelClass(isLight)}>Risk classification</label>
              <select className={fieldClass(isLight)} value={form.riskLevel} onChange={(e) => setForm((p) => ({ ...p, riskLevel: e.target.value as AccessPolicy['riskLevel'] }))}>
                {RISK_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>
            <div className={`rounded-xl border px-3 mt-4 ${isLight ? 'border-neutral-200' : 'border-neutral-800'}`}>
              <ToggleRow label="Allow tool / resource discovery" hint="Governed discovery — different users may see different lists." checked={form.allowDiscovery} onChange={(allowDiscovery) => setForm((p) => ({ ...p, allowDiscovery }))} isLight={isLight} />
              <ToggleRow label="Allow invocation" checked={form.allowInvocation} onChange={(allowInvocation) => setForm((p) => ({ ...p, allowInvocation }))} isLight={isLight} />
              <ToggleRow label="Read-only (no actions)" checked={form.readOnly} onChange={(readOnly) => setForm((p) => ({ ...p, readOnly }))} isLight={isLight} />
              <ToggleRow label="Require human approval" checked={form.requiresApproval} onChange={(requiresApproval) => setForm((p) => ({ ...p, requiresApproval }))} isLight={isLight} />
              <ToggleRow label="Sensitive-data restrictions" checked={form.sensitiveDataRestricted} onChange={(sensitiveDataRestricted) => setForm((p) => ({ ...p, sensitiveDataRestricted }))} isLight={isLight} />
              <ToggleRow label="Output restrictions" checked={form.outputRestricted} onChange={(outputRestricted) => setForm((p) => ({ ...p, outputRestricted }))} isLight={isLight} />
              <ToggleRow label="Inherit existing Selfic user, group, and capability permissions" checked={form.inheritSelficPermissions} onChange={(inheritSelficPermissions) => setForm((p) => ({ ...p, inheritSelficPermissions }))} isLight={isLight} />
            </div>
          </FormSection>
        </div>

        <div className="space-y-4">
          <section className={`${panelClass(isLight)} p-5`}>
            <h3 className={`text-sm font-semibold ${isLight ? 'text-neutral-900' : 'text-white'}`}>Coverage</h3>
            <dl className={`mt-3 space-y-3 text-sm ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>
              <div>
                <dt>Clients</dt>
                <dd className={isLight ? 'text-neutral-900' : 'text-white'}>{form.clientIds.length || 'Any'}</dd>
              </div>
              <div>
                <dt>MCP URL</dt>
                <dd className={`font-mono break-all ${isLight ? 'text-neutral-900' : 'text-white'}`}>{MCP_PUBLIC_URL}</dd>
              </div>
              <div>
                <dt>Capabilities</dt>
                <dd className={isLight ? 'text-neutral-900' : 'text-white'}>{form.publishedCapabilityIds.length || 'Any'}</dd>
              </div>
              <div>
                <dt>Risk</dt>
                <dd className={`capitalize ${isLight ? 'text-neutral-900' : 'text-white'}`}>{form.riskLevel}</dd>
              </div>
            </dl>
          </section>
        </div>
      </FormStack>
    </GatewayDrawer>
  );
}
